// 'use client';
import React from 'react';
import Link from 'next/link';

import { IRepository, IRepositoryEntry, TEntryDataType } from '@/types';
import emojify from '@/lib/emojify';
import * as format from '@/lib/format';
import { toTimeAgo } from '@/lib/dateTime';

import { BASE_API_URL } from '@/config/environment';

import * as Icon from '@/ui/Icon';
import Button from '@/ui/Button';
import Tag from '@/ui/Tag';
import Badge from '@/ui/Badge';
import HoverCard from '@/ui/HoverCard';
import { Row } from '@/ui/primatives';

import * as Styled from './styled';

const ImageFilePreview: React.FC<{ fileRootPath: string; entry: IRepositoryEntry }> = ({ fileRootPath, entry }) => {
  const fileUrl = new URL(fileRootPath);

  fileUrl.searchParams.append('height', '128');

  return entry.filename ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img style={{ height: 128, objectFit: 'contain', background: '#cccccc' }} src={fileUrl.href} alt={entry.filename} />
  ) : (
    <div>Loading...</div>
  );
};

const getEntryLinkPath = (
  entry: IRepositoryEntry,
  repository: IRepository,
  branch: string,
  folderPath: string | undefined,
) => {
  const pathLinkComponent = folderPath === './' ? '' : '/' + folderPath;
  const pathPrefix = `/${repository.namespace}/${repository.name}`;
  const pathSuffix = `${branch}${pathLinkComponent}/${entry.filename}`;
  const pathMode = entry.is_dir ? 'dir' : 'file';
  return [pathPrefix, pathMode, pathSuffix].join('/').replace(/\/\//g, '/'); // remove double slashes;
};

const ImageFileIcon: React.FC<{ fileRootPath: string; entry: IRepositoryEntry }> = ({ fileRootPath, entry }) => {
  return (
    <HoverCard
      trigger={
        <span>
          <Icon.ImageFile />
        </span>
      }
    >
      <ImageFilePreview fileRootPath={fileRootPath} entry={entry} />
    </HoverCard>
  );
};

const DirectoryEntry: React.FC<{
  entry: IRepositoryEntry;
  repository: IRepository;
  branch: string;
  openForEditing?: boolean;
  filenameOverride?: string;
  folderPath: string | undefined;
}> = ({ entry, repository, branch, openForEditing, filenameOverride, folderPath }) => {
  const linkPath = getEntryLinkPath(entry, repository, branch, folderPath);
  const lastCommitMessage = entry.latest_commit && emojify(entry.latest_commit.message);

  const fileRootPath = `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/file/${entry?.resource?.version}/${entry?.resource?.path}`;

  return (
    <Styled.DirectoryEntry key={entry.filename}>
      <Styled.File>
        {entry.is_dir ? (
          <Icon.FolderClosed />
        ) : entry.data_type === 'image' ? (
          <ImageFileIcon fileRootPath={fileRootPath} entry={entry} />
        ) : (
          <Icon.File
            style={{ color: openForEditing ? 'green' : 'inherit' }}
            onClick={() => console.log(entry.filename, entry)}
          />
        )}
        <span className="filename">
          <Link href={linkPath} className="link" title={entry.filename}>
            {filenameOverride || entry.filename}
          </Link>
        </span>
      </Styled.File>
      <div className="filesize">{entry.size >= 0 && <Tag small>{format.humanFileSize(entry.size)}</Tag>}</div>
      <Styled.CommitMessage>
        <span title={lastCommitMessage}>{lastCommitMessage}</span>
      </Styled.CommitMessage>
      <Row alignItems='center' gap={5}>
        <Button.Basic href={fileRootPath} download smaller>
          <Icon.Download />
        </Button.Basic>
        <Styled.FileType title={entry.mime_type} onClick={() => console.log('entry', entry)}>
          {entry.data_type !== 'dir' && (
            <Badge variant={entry.data_type} className="data-type-badge">
              {entry.data_type}
            </Badge>
          )}
        </Styled.FileType>
      </Row>
      <Styled.TimeAgo>{entry.latest_commit && toTimeAgo(new Date(entry.latest_commit.timestamp))}</Styled.TimeAgo>
    </Styled.DirectoryEntry>
  );
};

export default DirectoryEntry;
